import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';

import {
  HaynesProVesaComponentImagesModel,
  HaynesProVesaPinConnectionModel
} from '../typings/haynes-pro.vesa-advanced';

@Component({
  selector   : 'wor-haynes-pro-diagnostic-tool-vesa-ecu-pin-connector',
  styles: [ require('./haynes-pro.diagnostic-tool.vesa-ecu-pin-connector.component.scss') ],
  template: require('./haynes-pro.diagnostic-tool.vesa-ecu-pin-connector.component.html')
})
export class HaynesProDiagnosticToolVesaEcuPinConnectorComponent implements AfterViewInit, OnChanges {

  @Input() images         : HaynesProVesaComponentImagesModel;
  @Input() pinConnections : Array<HaynesProVesaPinConnectionModel> = [];

  @ViewChild('ecuSvgContainer') ecuSvgContainer             : ElementRef<HTMLDivElement>;
  @ViewChild('connectorSvgContainer') connectorSvgContainer : ElementRef<HTMLDivElement>;
  @ViewChild('pinListContainer') pinListContainer           : ElementRef<HTMLDivElement>;
  @ViewChildren('pinRow') pinRows                           : QueryList<ElementRef<HTMLDivElement>>;

  selectedRowIndex : number | null = null;
  hoveredRowIndex  : number | null = null;
  selectedPin      : string | null = null;
  hoveredPin       : string | null = null;
  isLoadingEcu                     = false;
  isLoadingConnector               = false;
  errorEcu                         = '';
  errorConnector                   = '';

  private ecuSvg       : SVGSVGElement | null = null;
  private connectorSvg : SVGSVGElement | null = null;

  private readonly SELECTED_CLASS = 'vesa-ecu-pin--selected';
  private readonly HOVER_CLASS    = 'vesa-ecu-pin--hover';
  // Rendered in the template before voltage values to read as "voltage flows
  // into this pin" — mirrors the existing `→` arrow used to denote
  // pin → target_pin direction. Public so the template can bind it.
  readonly VOLTAGE_BACK_ARROW = '← ';

  constructor (
    private cdr  : ChangeDetectorRef,
    private zone : NgZone
  ) {}

  // ── Lifecycle ──

  ngAfterViewInit () : void {
    setTimeout(() => this.loadBothSvgs());
  }

  ngOnChanges ( changes : SimpleChanges ) : void {
    if (changes.images && !changes.images.isFirstChange()) {
      this.selectedPin = null;
      this.hoveredPin  = null;

      setTimeout(() => this.loadBothSvgs());
    }
  }

  // ── Getters ──

  get hasConnections () : boolean {
    return this.pinConnections?.length > 0;
  }

  // ── Public methods ──

  // Returns voltage for a pin connection if present. Not yet wired into the
  // template — display of voltage on the ECU pin list is tracked in WS-6122.
  // This file is touched here only to align with the tightened `electrical`
  // type introduced in WS-6119 (removed unreachable `electrical.value` fallback).
  getVoltage ( conn : HaynesProVesaPinConnectionModel ) : string {
    return conn?.electrical?.voltage || '';
  }

  // Hovering a pin on the SVG — only react if this pin has a list entry.
  hoverPinFromSvg ( pinNumber : string | null ) : void {
    if (!pinNumber) {
      this.hoveredPin      = null;
      this.hoveredRowIndex = null;
    } else {
      const rowIndex = this.findRowIndexByPin(pinNumber);

      if (rowIndex < 0) {
        this.hoveredPin      = null;
        this.hoveredRowIndex = null;
      } else {
        this.hoveredPin      = pinNumber;
        this.hoveredRowIndex = rowIndex;
      }
    }

    this.applySvgHighlights();
  }

  hoverRow ( index : number | null, pinNumber : string | null ) : void {
    this.hoveredRowIndex = index === undefined ? null : index;
    this.hoveredPin      = pinNumber || null;
    this.applySvgHighlights();
  }

  isRowHovered ( index : number ) : boolean {
    return this.hoveredRowIndex === index;
  }

  isRowSelected ( index : number ) : boolean {
    return this.selectedRowIndex === index;
  }

  // Render the colour as a valid CSS colour — pass through named colours and
  // hex codes, fall back to gray for unknown tokens.
  resolveWireCss ( color : string ) : string {
    if (!color) {
      return '#d0d0d0';
    }

    return color;
  }

  // Clicking a pin on the SVG — only react if this pin has a list entry.
  selectPinFromSvg ( pinNumber : string ) : void {
    if (!pinNumber) {
      return;
    }

    const rowIndex = this.findRowIndexByPin(pinNumber);

    if (rowIndex < 0) {
      return;
    }

    if (this.selectedRowIndex === rowIndex) {
      this.selectedPin      = null;
      this.selectedRowIndex = null;
    } else {
      this.selectedPin      = pinNumber;
      this.selectedRowIndex = rowIndex;
    }

    this.applySvgHighlights();
    this.scrollPinRowIntoView(this.selectedRowIndex);
  }

  // Clicking a list row — single-row selection, plus SVG highlight for its pin.
  selectRow ( index : number, pinNumber : string ) : void {
    if (this.selectedRowIndex === index) {
      this.selectedRowIndex = null;
      this.selectedPin      = null;
    } else {
      this.selectedRowIndex = index;
      this.selectedPin      = pinNumber || null;
    }

    this.applySvgHighlights();
  }

  trackByRow ( index : number, row : HaynesProVesaPinConnectionModel ) : string {
    return `${row?.pin?.number}-${row?.component?.component_id}-${index}`;
  }

  // Wire colour at a given index (0 = source side, 1 = target side).
  wireColorAt ( conn : HaynesProVesaPinConnectionModel, index : number ) : string {
    return conn?.wire_colors?.[index] || '';
  }

  // ── Private methods ──

  private applyPinHighlight (
    svg       : SVGSVGElement,
    pinNumber : string,
    type      : 'selected' | 'hover'
  ) : void {
    const cssClass    = type === 'selected' ? this.SELECTED_CLASS : this.HOVER_CLASS;
    const stroke      = type === 'selected' ? '#0d8a8a' : '#f59e0b';
    const strokeWidth = type === 'selected' ? '4' : '3';
    const fill        = type === 'selected' ? 'rgba(13, 138, 138, 0.35)' : 'rgba(245, 158, 11, 0.3)';

    this.findPinElements(svg, pinNumber).forEach(( el : Element ) => {
      el.classList.add(cssClass);

      this.getStyleTargets(el).forEach(( target : SVGElement ) => {
        target.classList.add(cssClass);
        target.style.setProperty('stroke', stroke, 'important');
        target.style.setProperty('stroke-width', strokeWidth, 'important');

        if (target.tagName.toLowerCase() !== 'text') {
          target.style.setProperty('fill', fill, 'important');
        }
      });
    });
  }

  private applySvgHighlights () : void {
    ([ this.ecuSvg, this.connectorSvg ].filter(
      ( svg : SVGSVGElement | null ) : svg is SVGSVGElement => !!svg
    ))
    .forEach(( svg : SVGSVGElement ) => {
      // Clear any previous highlights — both classes AND inline styles.
      svg.querySelectorAll(`.${this.SELECTED_CLASS}, .${this.HOVER_CLASS}`)
      .forEach(( element : Element ) => {
        element.classList.remove(this.SELECTED_CLASS);
        element.classList.remove(this.HOVER_CLASS);
        (element as SVGElement).style.removeProperty('stroke');
        (element as SVGElement).style.removeProperty('stroke-width');
        (element as SVGElement).style.removeProperty('fill');
      });

      if (this.selectedPin) {
        this.applyPinHighlight(svg, this.selectedPin, 'selected');
      }

      if (this.hoveredPin && this.hoveredPin !== this.selectedPin) {
        this.applyPinHighlight(svg, this.hoveredPin, 'hover');
      }
    });
  }

  private bindSvgPinInteractions ( svg : SVGSVGElement ) : void {
    // Text pin labels sit over the rects — make them pass through pointer
    // events so hovering the number keeps the rect's hover active.
    svg.querySelectorAll('text').forEach(( textEl : Element ) => {
      (textEl as SVGElement).style.setProperty('pointer-events', 'none', 'important');
    });

    // Find all elements whose id looks like a pin reference (pin1, pin-1, pin_1, etc.)
    const pinElements = Array.from(svg.querySelectorAll('[id]'))
    .filter(( el : Element ) => /^pin[-_]?[A-Za-z0-9]+/i.test(el.id));

    pinElements.forEach(( element : Element ) => {
      const pinNumber = this.extractPinNumber(element.id);

      if (!pinNumber) {
        return;
      }

      const targets = this.getStyleTargets(element);

      // Unused pins — no corresponding list row, so not interactive.
      if (this.findRowIndexByPin(pinNumber) < 0) {
        targets.forEach(( target : SVGElement ) => {
          target.style.setProperty('cursor', 'default', 'important');
        });

        return;
      }

      targets.forEach(( target : SVGElement ) => {
        target.style.cursor        = 'pointer';
        target.style.pointerEvents = 'visible';
      });

      element.addEventListener('click', ( event : Event ) => {
        event.preventDefault();
        event.stopPropagation();

        this.zone.run(() => {
          this.selectPinFromSvg(pinNumber);
          this.cdr.detectChanges();
        });
      });

      element.addEventListener('mouseenter', () => {
        this.zone.run(() => {
          this.hoverPinFromSvg(pinNumber);
          this.cdr.detectChanges();
        });
      });

      element.addEventListener('mouseleave', () => {
        this.zone.run(() => {
          this.hoverPinFromSvg(null);
          this.cdr.detectChanges();
        });
      });
    });
  }

  private extractPinNumber ( id : string ) : string {
    const match = id.match(/^pin[-_]?([A-Za-z0-9]+)/i);

    return match ? this.normalizePin(match[1]) : '';
  }

  private findPinElements ( svg : SVGSVGElement, pinNumber : string ) : Array<Element> {
    if (!pinNumber) {
      return [];
    }

    // Normalize both sides — data values can contain spaces ("A 1") while SVG ids are compact ("pin-A1").
    const normalized = this.normalizePin(pinNumber);

    const matches = Array.from(svg.querySelectorAll('[id]'))
    .filter(( el : Element ) => this.extractPinNumber(el.id) === normalized);

    return matches;
  }

  private findRowIndexByPin ( pinNumber : string ) : number {
    const normalized = this.normalizePin(pinNumber);

    return this.pinConnections.findIndex(
      ( row : HaynesProVesaPinConnectionModel ) => this.normalizePin(row?.pin?.number) === normalized
    );
  }

  private getStyleTargets ( element : Element ) : Array<SVGElement> {
    if (element.tagName.toLowerCase() === 'g') {
      return Array.from(
        element.querySelectorAll('rect, path, polygon, polyline, circle, ellipse, line, text')
      ) as Array<SVGElement>;
    }

    return [ element as SVGElement ];
  }

  private async loadBothSvgs () : Promise<void> {
    if (this.images?.ecu_side && this.ecuSvgContainer?.nativeElement) {
      this.isLoadingEcu = true;
      this.errorEcu     = '';

      try {
        this.ecuSvg = await this.loadSvg(this.images.ecu_side, this.ecuSvgContainer);

        if (this.ecuSvg) {
          this.bindSvgPinInteractions(this.ecuSvg);
        }
      } catch (err) {
        this.errorEcu = (err as Error)?.message || 'Unable to load control unit image';
      } finally {
        this.isLoadingEcu = false;
      }
    }

    if (this.images?.connector_side && this.connectorSvgContainer?.nativeElement) {
      this.isLoadingConnector = true;
      this.errorConnector     = '';

      try {
        this.connectorSvg = await this.loadSvg(this.images.connector_side, this.connectorSvgContainer);

        if (this.connectorSvg) {
          this.bindSvgPinInteractions(this.connectorSvg);
        }
      } catch (err) {
        this.errorConnector = (err as Error)?.message || 'Unable to load connector image';
      } finally {
        this.isLoadingConnector = false;
      }
    }

    this.cdr.detectChanges();
  }

  private async loadSvg (
    url       : string,
    container : ElementRef<HTMLDivElement>
  ) : Promise<SVGSVGElement | null> {
    const response = await fetch(url, { mode: 'cors' });

    if (!response.ok) {
      throw new Error(`Failed to fetch ECU image (${response.status})`);
    }

    const svgText = await response.text();
    const parsed  = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    const svg     = parsed.documentElement as unknown as SVGSVGElement;

    svg.removeAttribute('width');
    svg.removeAttribute('height');
    svg.style.width  = '100%';
    svg.style.height = 'auto';

    container.nativeElement.innerHTML = '';
    container.nativeElement.appendChild(svg);

    return svg;
  }

  private normalizePin ( value : string ) : string {
    return (value || '').replace(/\s+/g, '').toUpperCase();
  }

  private scrollPinRowIntoView ( rowIndex : number | null ) : void {
    if (rowIndex === null || rowIndex < 0 || !this.pinRows) {
      return;
    }

    setTimeout(() => {
      const container = this.pinListContainer?.nativeElement;
      const row       = this.pinRows.toArray()[rowIndex]?.nativeElement;

      if (!container || !row) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const rowRect       = row.getBoundingClientRect();
      const nextTop       = container.scrollTop + (rowRect.top - containerRect.top);

      container.scrollTo({
        top     : Math.max(nextTop, 0),
        behavior: 'smooth'
      });
    });
  }
}
